// src/sectors/pilates/settings/tabs/sections/company/CompanyToolsSection.jsx
import React from 'react';
import PropTypes from 'prop-types';

import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Icon from '@mui/material/Icon';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { SectionCard } from '../../ui/SettingsUi';

const CAL_TYPES = [
  {
    value: 'TAM_SAAT',
    label: 'Tam Saat (12:00–13:00)',
    helper: 'Slotlar 60dk ve saat başı başlar.',
  },
  {
    value: 'YARIM_SAAT_TAM',
    label: 'Yarım Saat – 60dk (12:30–13:30)',
    helper: 'Slotlar 60dk ama 30dk kaydırmalı başlar.',
  },
  {
    value: 'YARIM_SAAT_YARIM',
    label: 'Yarım Saat – 30dk (12:30–13:00)',
    helper: 'Slotlar 30dk ve 30dk kaydırmalı başlar.',
  },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${String(h).padStart(2, '0')}:00`,
}));

export function CompanyToolsSection({
  loading,
  error,
  calendarType,
  setCalendarType,

  dayStartHour,
  setDayStartHour,
  dayEndHour,
  setDayEndHour,

  dirty,
  onEdit,
  onCancel,
  onSave,
  editMode,
  saving = false,
}) {
  const theme = useTheme();

  const accent = '#A9D6F5';
  const green = theme.palette.success.main;

  const isHalfMode =
    String(calendarType) === 'YARIM_SAAT_TAM' || String(calendarType) === 'YARIM_SAAT_YARIM';

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2.25,
      backgroundColor: alpha(theme.palette.common.white, 0.6),
      backdropFilter: 'blur(8px)',
      transition: 'all 160ms ease',

      '& fieldset': {
        borderColor: alpha(theme.palette.text.primary, 0.12),
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: alpha(theme.palette.text.primary, 0.16),
      },
      '&.Mui-focused fieldset': { borderColor: alpha(accent, 0.35) },
      '&.Mui-focused': { boxShadow: `0 0 0 4px ${alpha(accent, 0.12)}` },
    },
    '& .MuiInputLabel-root': { fontSize: 12.5 },
    '& .MuiFormHelperText-root': { opacity: 0.7 },
  };

  const selectedCal =
    CAL_TYPES.find((x) => String(x.value) === String(calendarType)) || CAL_TYPES[0];

  const startOpt =
    HOUR_OPTIONS.find((x) => Number(x.value) === Number(dayStartHour)) || HOUR_OPTIONS[8];
  const endOpt =
    HOUR_OPTIONS.find((x) => Number(x.value) === Number(dayEndHour)) || HOUR_OPTIONS[22];

  const localError = React.useMemo(() => {
    const s = Number(dayStartHour);
    const e = Number(dayEndHour);
    if (!Number.isFinite(s) || !Number.isFinite(e)) return null;
    if (e <= s) return 'Gün bitiş saati, başlangıç saatinden büyük olmalı.';
    return null;
  }, [dayStartHour, dayEndHour]);

  const disableAll = !editMode || loading || saving;

  return (
    <SectionCard
      icon="build"
      title="Araçlar"
      subtitle="Takvim ve diğer basit ayarlar"
      right={
        loading ? (
          <MDBox sx={{ display: 'grid', placeItems: 'center', width: 28, height: 28 }}>
            <CircularProgress size={18} />
          </MDBox>
        ) : (
          <Chip
            size="small"
            variant="outlined"
            label={dirty ? 'Değişiklik var' : 'Kaydedildi'}
            sx={{ borderRadius: '999px', fontWeight: 900 }}
          />
        )
      }
    >
      {error ? (
        <MDTypography variant="caption" sx={{ color: 'error.main', fontWeight: 900, mb: 1 }}>
          {error}
        </MDTypography>
      ) : null}

      {localError ? (
        <MDTypography variant="caption" sx={{ color: 'warning.main', fontWeight: 900, mb: 1 }}>
          {localError}
        </MDTypography>
      ) : null}

      <MDBox sx={{ display: 'grid', gap: 1.1 }}>
        {/* ✅ Takvim zaman tipi */}
        <Autocomplete
          size="small"
          options={CAL_TYPES}
          value={selectedCal}
          disabled={disableAll}
          onChange={(e, v) => setCalendarType(v?.value || 'TAM_SAAT')}
          getOptionLabel={(o) => o?.label || ''}
          isOptionEqualToValue={(o, v) => String(o.value) === String(v.value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Takvim Zaman Tipi"
              sx={inputSx}
              helperText={selectedCal?.helper || 'Takvim satırlarını nasıl göstereceğini belirler.'}
            />
          )}
          slotProps={{
            paper: {
              sx: {
                mt: 0.7,
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.92),
                backdropFilter: 'blur(12px)',
                boxShadow: `0 14px 34px ${alpha(theme.palette.common.black, 0.12)}`,
              },
            },
            listbox: {
              sx: { p: 0.75 },
            },
          }}
        />

        {/* 🔒 Slot Yuvarlama (Şimdilik pasif) */}
        <MDBox
          sx={{
            opacity: 0.6,
            border: `1px dashed ${alpha(theme.palette.text.primary, 0.15)}`,
            borderRadius: 2,
            p: 1.2,
          }}
        >
          <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.5 }}>
            <Icon fontSize="small">lock</Icon>
            <MDTypography fontWeight="bold">Slot Yuvarlama Yönü</MDTypography>
          </MDBox>

          <FormControl fullWidth size="small" disabled>
            <Select value="FLOOR">
              <MenuItem value="FLOOR">Aşağı Yuvarla (Varsayılan)</MenuItem>
              <MenuItem value="CEIL">Yukarı Yuvarla</MenuItem>
              <MenuItem value="NEAREST">En Yakın Slot</MenuItem>
            </Select>
          </FormControl>

          <MDTypography variant="caption" sx={{ display: 'block', mt: 0.6, opacity: 0.7 }}>
            Yakında eklenecek: Mevcut seansların yeni takvim tipine otomatik taşınması ve yuvarlama
            seçimi.
          </MDTypography>

          {/* İstersen sadece yarım saat modunda göster */}
          {/* {!isHalfMode ? null : (...)} */}
        </MDBox>

        {/* ✅ Gün başlangıç / bitiş */}
        <MDBox
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1,
          }}
        >
          <Autocomplete
            size="small"
            options={HOUR_OPTIONS}
            value={startOpt}
            disabled={disableAll}
            onChange={(e, v) => {
              const next = Number(v?.value);
              if (!Number.isFinite(next)) return;
              setDayStartHour(next);

              if (Number(dayEndHour) <= next) setDayEndHour(Math.min(23, next + 1));
            }}
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(o, v) => Number(o.value) === Number(v.value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Gün Başlangıç"
                sx={inputSx}
                helperText="Takvimde ilk görünen saat."
              />
            )}
          />

          <Autocomplete
            size="small"
            options={HOUR_OPTIONS.filter((x) => Number(x.value) > Number(dayStartHour))}
            value={endOpt}
            disabled={disableAll}
            onChange={(e, v) => {
              const next = Number(v?.value);
              if (!Number.isFinite(next)) return;
              setDayEndHour(next);
            }}
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(o, v) => Number(o.value) === Number(v.value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Gün Bitiş"
                sx={inputSx}
                helperText="Takvimde son görünen saat."
              />
            )}
          />
        </MDBox>

        <Divider sx={{ my: 0.5 }} />

        {!editMode ? (
          <MDBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Chip
              label="Düzenle"
              variant="outlined"
              onClick={onEdit}
              sx={{ borderRadius: '999px', fontWeight: 900, cursor: 'pointer' }}
            />
          </MDBox>
        ) : (
          <Stack direction="row" spacing={0.8} justifyContent="flex-end">
            <Chip
              label="Vazgeç"
              variant="outlined"
              onClick={onCancel}
              disabled={saving}
              sx={{ borderRadius: '999px', fontWeight: 900, cursor: 'pointer' }}
            />
            <Chip
              label={saving ? 'Kaydediliyor…' : 'Kaydet'}
              color="success"
              onClick={onSave}
              disabled={saving || !dirty || !!localError}
              sx={{
                borderRadius: '999px',
                fontWeight: 900,
                cursor: saving || !dirty || !!localError ? 'default' : 'pointer',
                bgcolor: alpha(green, 0.12),
                border: `1px solid ${alpha(green, 0.22)}`,
              }}
              icon={saving ? <CircularProgress size={14} /> : <Icon>done</Icon>}
            />
          </Stack>
        )}
      </MDBox>
    </SectionCard>
  );
}

CompanyToolsSection.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,

  calendarType: PropTypes.string,
  setCalendarType: PropTypes.func.isRequired,

  dayStartHour: PropTypes.number,
  setDayStartHour: PropTypes.func.isRequired,
  dayEndHour: PropTypes.number,
  setDayEndHour: PropTypes.func.isRequired,

  dirty: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
  saving: PropTypes.bool,
};
